import {
  Alert,
  Anchor,
  Box,
  Button,
  Center,
  Checkbox,
  Loader,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type SignUpInput, signUpInputSchema } from '@wwaave/shared/zod';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';

import { AuthShell } from '../components/auth-shell/AuthShell';
import { WaitlistCardShell, WaitlistForm } from '../components/marketing/WaitlistForm';
import { ChoiceCard } from '../components/wizard/ChoiceCard';
import { Wizard, type WizardStep } from '../components/wizard/Wizard';
import { mutateJson } from '../lib/api';
import { useAuth, useWaitlistMode } from '../lib/auth-context';
import { clearReferralCookie, readReferralCookie } from '../lib/referral-cookie';

const TERMS_VERSION = '2026-05-01';

// AUD-004 — `kind` discriminates the two 200 shapes the worker can return.
// `collision` carries no `user` field; consumers must branch on `kind` BEFORE
// touching `.user`.
type SignUpResponse =
  | { kind: 'created'; user: { role: 'artist' | 'host' } }
  | { kind: 'collision'; message: string };

/**
 * AUD-035 — resolve the referring artist's display name for the banner,
 * falling back to the raw slug while loading or on a 404. Reads the
 * referrer endpoint, which (unlike /artists/$slug) answers for unpublished
 * artists too.
 */
async function fetchReferralArtistName(slug: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/v/referrers/${encodeURIComponent(slug)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { displayName?: unknown };
    return typeof data.displayName === 'string' ? data.displayName : null;
  } catch {
    return null;
  }
}

/**
 * Launch-aware entry point. When public sign-ups are open, this is the normal
 * account-creation wizard. When they're closed, the page adapts to the launch
 * stage instead of letting the visitor fill the whole form and hit a 403:
 *   - Waitlist mode ON  → the waitlist capture (seeded to the ?role segment).
 *   - Waitlist mode OFF → a plain "not open yet" notice.
 *
 * The server (routes/auth.ts) is still the hard gate; this only shapes the
 * client funnel. We gate on `isLoading` so we never flash the form before the
 * stage resolves (KV-cached, ~instant on a warm cache).
 */
function SignUpScreen() {
  const { signupsOpen, waitlistMode, isLoading, loaded } = useWaitlistMode();
  const role = Route.useSearch({ select: (s) => (s.role === 'host' ? 'host' : 'artist') });
  // Secret preview link (`?preview=<token>`, worker lib/signup-preview.ts) —
  // show the form even while sign-ups are closed. The server checks the token;
  // a wrong one just gets the normal "not accepting sign-ups" error on submit.
  const hasPreviewToken = Route.useSearch({ select: (s) => s.preview !== undefined });

  if (isLoading) {
    return (
      <AuthShell>
        <Center mih={280}>
          <Loader color="brand" />
        </Center>
      </AuthShell>
    );
  }

  // Closed to the public (and the stage is known) — adapt rather than 403.
  if (loaded && !signupsOpen && !hasPreviewToken) {
    return waitlistMode ? <SignUpWaitlistPanel role={role} /> : <SignUpClosedPanel />;
  }

  // Open, or the stage read failed (fail open — the server still gates).
  return <SignUpForm />;
}

/**
 * Waitlist branch — shown at /sign-up while sign-ups are closed and Waitlist
 * mode is on, so every existing "Sign up" CTA across the marketing site funnels
 * to lead capture instead of a dead end. The visitor's artist/host intent from
 * `?role` seeds the segment.
 */
function SignUpWaitlistPanel({ role }: { role: 'artist' | 'host' }) {
  return (
    <AuthShell>
      <Stack gap="xl">
        <Box>
          <Text
            size="xs"
            tt="uppercase"
            c="brand.7"
            fw={600}
            style={{ letterSpacing: '0.1em' }}
          >
            Not open just yet
          </Text>
          <Title
            order={1}
            mt={6}
            style={{
              fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
            }}
          >
            Join the waitlist.
          </Title>
          <Text c="ink.6" mt={6} style={{ lineHeight: 1.55 }}>
            We&apos;re not accepting new accounts yet — WWAAVE is opening in SE QLD first, one
            region at a time. Leave your details and we&apos;ll reach out the moment sign-ups open
            near you.
          </Text>
        </Box>
        <WaitlistCardShell>
          <WaitlistForm
            initialLeadType={role === 'host' ? 'venue' : 'artist'}
            showLeadDetails
            copy={{
              eyebrow: 'GET EARLY ACCESS',
              headline: "We'll save you a spot.",
              body: "Tell us who you are and we'll email you the moment sign-ups open near you.",
              submitLabel: 'Get Early Access',
              successEyebrow: "YOU'RE ON THE LIST",
              successHeadline: 'Thanks — we’ll be in touch.',
              successBody:
                "We'll email you the moment sign-ups open in your area. No spam, ever.",
            }}
          />
        </WaitlistCardShell>
        <Text size="sm" ta="center" c="ink.6">
          Already have an account?{' '}
          <Anchor component={Link} to="/sign-in" c="brand.7" fw={500}>
            Sign in
          </Anchor>
        </Text>
      </Stack>
    </AuthShell>
  );
}

/**
 * Closed branch — sign-ups off and Waitlist mode off. A quiet holding notice so
 * a stray "Sign up" click reads as intentional, not broken.
 */
function SignUpClosedPanel() {
  return (
    <AuthShell>
      <Stack gap="lg">
        <Box>
          <Title
            order={1}
            style={{
              fontSize: 'clamp(1.6rem, 3.5vw, 2.1rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
            }}
          >
            We&apos;re not open for sign-ups yet.
          </Title>
          <Text c="ink.6" mt={10} style={{ lineHeight: 1.55 }}>
            WWAAVE is opening in SE QLD first. Sign-ups aren&apos;t live yet — check back soon, or
            have a look around while you&apos;re here.
          </Text>
        </Box>
        <Stack gap="xs">
          <Button component={Link} to="/how-it-works" variant="light" color="brand">
            See how it works
          </Button>
          <Text size="sm" ta="center" c="ink.6">
            Already have an account?{' '}
            <Anchor component={Link} to="/sign-in" c="brand.7" fw={500}>
              Sign in
            </Anchor>
          </Text>
        </Stack>
      </Stack>
    </AuthShell>
  );
}

type SignUpValues = Omit<SignUpInput, 'accepted_covenant' | 'age_affirmed'> & {
  // Checkbox state; the schema's literal-true rule is what decides on submit.
  accepted_covenant: boolean;
  age_affirmed: boolean;
};

// Which fields each step owns, in step order — used to send the visitor back
// to the right screen if the final whole-form check fails.
const STEP_FIELDS: (keyof SignUpValues)[][] = [
  ['role'],
  ['display_name'],
  ['email'],
  ['password'],
  ['accepted_covenant', 'age_affirmed'],
];

/**
 * Sign-up as a one-question-per-screen wizard: who you are → name → email →
 * password → the fine print. Nothing is sent until the last step, so a
 * half-finished sign-up leaves no account behind. Each step validates only its
 * own field (against the shared schema) before moving on.
 */
function SignUpForm() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  // `?role=artist|host` is a pre-segmentation hint set by upstream CTAs
  // (homepage StepInit, /founding-venues). It preselects the first answer;
  // the visitor still confirms it.
  const search = Route.useSearch();
  const [step, setStep] = useState(0);
  // AUD-004: the email-collision response is informational, not an error —
  // rendered as its own calm state with sign-in / reset paths, since no email
  // is actually sent on collision and a red alert would read as failure.
  const [collisionNotice, setCollisionNotice] = useState<string | null>(null);
  // `?ref=<artistSlug>` — the artist referral link (North Star §7). Captured at
  // sign-up; the worker resolves the slug → artist user id and either stamps it
  // onto the venue_account (host) or stashes a cookie for the host-onboarding
  // moment (artist who later adds host).
  //
  // Fall back to the `wwaave_ref_share` cookie when the URL has no `?ref=` —
  // a visitor who landed on the homepage via `?r=<slug>` (cookie set there)
  // and then clicked Sign up still gets attribution. URL param wins so a
  // share link can over-rule a stale cookie.
  const refArtistSlug = search.ref ?? readReferralCookie();

  // AUD-035 — resolve the referring artist's name for the banner. Falls back
  // to the raw slug while loading or if the lookup 404s.
  const referralNameQuery = useQuery({
    queryKey: ['referral-artist-name', refArtistSlug],
    queryFn: () => fetchReferralArtistName(refArtistSlug as string),
    enabled: refArtistSlug !== null && refArtistSlug.length > 0,
  });
  const referralDisplayName = referralNameQuery.data ?? refArtistSlug;

  const form = useForm<SignUpValues>({
    initialValues: {
      role: search.role === 'host' ? 'host' : 'artist',
      email: '',
      password: '',
      display_name: '',
      accepted_terms_version: TERMS_VERSION,
      // AUD-021: a real, unticked checkbox — the schema's literal-true rule
      // rejects `false` with an inline error.
      accepted_covenant: false,
      // Starts unticked so the 18+ affirmation is a deliberate act.
      age_affirmed: false,
    },
    validate: zod4Resolver(signUpInputSchema),
  });
  const role = form.values.role;

  /** Validate just these fields; false (errors showing) keeps the step open. */
  function stepValid(fields: (keyof SignUpValues)[]): boolean {
    let ok = true;
    for (const f of fields) if (form.validateField(f).hasError) ok = false;
    return ok;
  }

  function chooseRole(next: 'artist' | 'host') {
    form.setFieldValue('role', next);
    setStep(1);
  }

  async function createAccount(): Promise<boolean> {
    if (!stepValid(['accepted_covenant', 'age_affirmed'])) return false;
    // Everything earlier was checked on its own step, but re-check the whole
    // form in case a value changed since (e.g. after going Back).
    const all = form.validate();
    if (all.hasErrors) {
      const firstBad = STEP_FIELDS.findIndex((fields) =>
        fields.some((f) => all.errors[f] !== undefined),
      );
      if (firstBad >= 0) setStep(firstBad);
      return false;
    }
    setCollisionNotice(null);
    const body: SignUpValues & { ref_artist_slug?: string; preview_token?: string } = {
      ...form.values,
      accepted_terms_version: TERMS_VERSION,
    };
    if (refArtistSlug !== null && refArtistSlug.length > 0) body.ref_artist_slug = refArtistSlug;
    if (search.preview !== undefined) body.preview_token = search.preview;

    const data = await mutateJson<SignUpResponse>('/api/auth/sign-up', body);
    // AUD-004: branch on `kind` BEFORE touching `.user` — the collision
    // shape doesn't carry one.
    if (data.kind === 'collision') {
      setCollisionNotice(data.message);
      return false;
    }
    // Clear the share cookie once the worker has the attribution. The worker
    // maintains its own server-side `REF_COOKIE` for the artist→add-capability
    // (host) cross-flow, so attribution survives even after this one drops.
    clearReferralCookie();
    await refresh();
    // Artists land on the profile-onboarding wizard; hosts go to the subscribe
    // step (/onboarding/host), which continues to venue setup once they're
    // subscribed, or immediately when payments aren't on for them.
    void navigate({ to: data.user.role === 'artist' ? '/onboarding/artist' : '/onboarding/host' });
    // Stay on this screen while the navigation happens.
    return false;
  }

  const steps: WizardStep[] = [
    {
      id: 'role',
      label: 'Artist or host',
      title: 'Which one are you?',
      lede: 'Pick the side you’re joining on. You can add the other one later.',
      hideNext: true,
      body: (
        <Stack gap="sm" role="radiogroup" aria-label="I'm joining as">
          {refArtistSlug !== null && (
            <Text size="sm" c="brand.7" fw={500}>
              You&apos;re joining via {referralDisplayName}&apos;s invite.
            </Text>
          )}
          {/* Focus lands on the selected card (radio-group convention), so a
              ?role=host link doesn't put the focus ring on the artist card. */}
          <ChoiceCard
            focusFirst={role !== 'host'}
            selected={role === 'artist'}
            onSelect={() => chooseRole('artist')}
            title="I’m an artist"
            description="I tour, and I’m looking for rooms to play and places to stay."
          />
          <ChoiceCard
            tone="tide"
            focusFirst={role === 'host'}
            selected={role === 'host'}
            onSelect={() => chooseRole('host')}
            title="I’m a host"
            description="I have a venue, a room or a stage, and I want to host touring artists."
          />
          <Text size="xs" c="dimmed" mt={4}>
            {role === 'host'
              ? 'What’s ahead: your account (about a minute), a monthly subscription, then your venue details.'
              : 'What’s ahead: your account (about a minute), then your profile. Always free for artists.'}
          </Text>
        </Stack>
      ),
    },
    {
      id: 'name',
      label: 'Name',
      title:
        role === 'host' ? 'What’s your venue or organisation called?' : 'What should we call you?',
      lede:
        role === 'host'
          ? 'This is the host name artists see. You’ll add individual venues after.'
          : 'Your artist or act name: the one hosts and other members will see.',
      onNext: () => stepValid(['display_name']),
      body: (
        <TextInput
          data-autofocus
          size="lg"
          aria-label={role === 'host' ? 'Venue or organisation name' : 'Display name'}
          placeholder={role === 'host' ? 'e.g. The Shed' : 'e.g. Wren and the Wanderers'}
          autoComplete={role === 'host' ? 'organization' : 'name'}
          {...form.getInputProps('display_name')}
        />
      ),
    },
    {
      id: 'email',
      label: 'Email',
      title: 'What’s your email?',
      lede: 'You’ll sign in with it. We never share it.',
      onNext: () => stepValid(['email']),
      body: (
        <TextInput
          data-autofocus
          size="lg"
          aria-label="Email"
          placeholder="you@example.com"
          autoComplete="email"
          type="email"
          {...form.getInputProps('email')}
        />
      ),
    },
    {
      id: 'password',
      label: 'Password',
      title: 'Choose a password.',
      lede: 'At least 10 characters. A short phrase is easier to remember than a jumble.',
      onNext: () => stepValid(['password']),
      body: (
        <PasswordInput
          data-autofocus
          size="lg"
          aria-label="Password"
          autoComplete="new-password"
          {...form.getInputProps('password')}
        />
      ),
    },
    {
      id: 'agree',
      label: 'Agree',
      title: 'Last thing: the fine print.',
      lede: 'Tick both and your account is ready.',
      nextLabel: 'Create account',
      onNext: createAccount,
      body: (
        <Stack gap="md">
          <Checkbox
            data-autofocus
            label={
              <Text size="sm">
                I agree to the{' '}
                <Anchor component={Link} to="/terms" target="_blank">
                  Terms
                </Anchor>{' '}
                and{' '}
                <Anchor component={Link} to="/privacy" target="_blank">
                  Privacy Policy
                </Anchor>
                , and to the WWAAVE Promise. No on-platform money, mutual exchange, mutual
                respect.
              </Text>
            }
            {...form.getInputProps('accepted_covenant', { type: 'checkbox' })}
          />
          <Checkbox
            label={<Text size="sm">I confirm I am 18 or older.</Text>}
            {...form.getInputProps('age_affirmed', { type: 'checkbox' })}
          />
          {collisionNotice !== null && (
            <Alert color="tide" variant="light">
              <Stack gap={6}>
                <Text size="sm">{collisionNotice}</Text>
                <Text size="sm">
                  <Anchor component={Link} to="/sign-in" fw={600}>
                    Sign in
                  </Anchor>{' '}
                  or{' '}
                  <Anchor component={Link} to="/reset-password" fw={600}>
                    reset your password
                  </Anchor>{' '}
                  if that account is yours, or{' '}
                  <Anchor component="button" type="button" fw={600} onClick={() => setStep(2)}>
                    use a different email
                  </Anchor>
                  .
                </Text>
              </Stack>
            </Alert>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <AuthShell
      footnote={
        <>
          Already have an account?{' '}
          <Anchor component={Link} to="/sign-in" c="brand.7" fw={500}>
            Sign in
          </Anchor>
        </>
      }
    >
      <Wizard
        eyebrow="Join WWAAVE"
        steps={steps}
        index={step}
        onIndexChange={setStep}
        tone={role === 'host' ? 'tide' : 'brand'}
      />
    </AuthShell>
  );
}

type SignUpSearch = {
  role?: 'artist' | 'host';
  ref?: string;
  preview?: string;
};

// Artist slug pattern — lowercased a-z0-9 with single hyphens, ≤64 chars. We
// validate at the search-param boundary so a malformed `?ref=` never reaches the
// API; the worker re-validates server-side anyway.
const REF_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
// Secret preview-link token — shape check only; the worker does the real match.
const PREVIEW_TOKEN_RE = /^[A-Za-z0-9_-]{32,128}$/;

export const Route = createFileRoute('/sign-up')({
  // `?role=artist|host` is a pre-segmentation hint from upstream CTAs (homepage,
  // /founding-venues). `?ref=<artistSlug>` is the artist referral link;
  // validate loosely — accept either of the two roles or none, and only accept
  // a slug-shaped ref. Let the form seed initialValues from these.
  validateSearch: (s: Record<string, unknown>): SignUpSearch => {
    const out: SignUpSearch = {};
    const r = s.role;
    if (r === 'artist' || r === 'host') out.role = r;
    const ref = s.ref;
    if (typeof ref === 'string' && REF_SLUG_RE.test(ref)) out.ref = ref;
    const preview = s.preview;
    if (typeof preview === 'string' && PREVIEW_TOKEN_RE.test(preview)) out.preview = preview;
    return out;
  },
  component: SignUpScreen,
});
